import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { businessApi, eventApi, paymentApi } from '../services/api';
import { toast } from 'sonner';
import { Building2, Calendar, Star, Clock, CheckCircle, XCircle, Plus, CreditCard, TrendingUp, Eye, BarChart3, MessageSquare } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const BusinessAccount = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isBusinessOwner, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [business, setBusiness] = useState(null);
  const [events, setEvents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isBusinessOwner) {
      navigate('/business-login');
      return;
    }
    loadData();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId && searchParams.get('payment') === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [isAuthenticated, isBusinessOwner, authLoading, navigate, searchParams]);

  const loadData = async () => {
    try {
      const [businessRes, eventsRes, paymentsRes] = await Promise.all([
        businessApi.getMine().catch(() => ({ data: null })),
        eventApi.getMine(),
        paymentApi.getHistory()
      ]);
      setBusiness(businessRes.data);
      setEvents(eventsRes.data);
      setPayments(paymentsRes.data);
      
      // Load analytics if business exists
      if (businessRes.data?.id) {
        try {
          const analyticsRes = await businessApi.getAnalytics(businessRes.data.id);
          setAnalytics(analyticsRes.data);
        } catch {
          // Analytics may fail silently
        }
        try {
          const rsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/businesses/${businessRes.data.id}/reviews`);
          setReviewStats({
            average_rating: rsRes.data?.average_rating,
            count: rsRes.data?.count || 0,
          });
        } catch {
          // ignore
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Payment is being processed. Please check back soon.');
      return;
    }
    
    try {
      const response = await paymentApi.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your featured status is now active.');
        loadData();
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="badge badge-status-approved flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approved</span>;
      case 'rejected':
        return <span className="badge badge-status-rejected flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>;
      default:
        return <span className="badge badge-status-pending flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const totalSpent = payments.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen bg-neutral-50 py-8" data-testid="business-account">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Welcome, {user?.name}!
          </h1>
          <p className="text-neutral-600">Business Owner Dashboard</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-6 h-6 opacity-80" />
              <span className="text-sm font-medium opacity-80">Business Status</span>
            </div>
            <p className="text-2xl font-bold">
              {business ? (business.status === 'approved' ? 'Live' : business.status) : 'No Listing'}
            </p>
          </div>
          
          <div className="stat-card green">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-6 h-6 opacity-80" />
              <span className="text-sm font-medium opacity-80">My Events</span>
            </div>
            <p className="text-2xl font-bold">{events.length}</p>
          </div>
          
          <div className="stat-card orange">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-6 h-6 opacity-80" />
              <span className="text-sm font-medium opacity-80">Total Invested</span>
            </div>
            <p className="text-2xl font-bold">${totalSpent.toFixed(2)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* My Business */}
            <div className="dashboard-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-neutral-900">My Business</h2>
                {!business && (
                  <Link to="/submit-business" className="btn-primary text-sm flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Listing
                  </Link>
                )}
              </div>

              {business ? (
                <div className="p-6 bg-neutral-50 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-secondary to-secondary-light flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      {business.photos?.[0] ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${business.photos[0]}`} alt="" className="w-full h-full object-cover rounded-xl" />
                      ) : (
                        business.business_name.charAt(0)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-xl font-bold text-neutral-900">{business.business_name}</h3>
                        {getStatusBadge(business.status)}
                      </div>
                      <p className="text-neutral-600 mb-2">{business.primary_category}</p>
                      <p className="text-sm text-neutral-500 mb-3">{business.street_address}, {business.city_state_zip}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {business.is_featured ? (
                          <span className="badge badge-featured flex items-center gap-1">
                            <Star className="w-3 h-3" /> Featured until {new Date(business.featured_until).toLocaleDateString()}
                          </span>
                        ) : business.status === 'approved' ? (
                          <Link to={`/feature-my-business?business=${business.id}`} className="text-sm text-accent font-semibold hover:underline">
                            Feature your business →
                          </Link>
                        ) : null}
                        
                        {business.featured_free && (
                          <span className="badge bg-green-100 text-green-700 border-green-200">Free Featured Month</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {business.status === 'rejected' && business.rejection_reason && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700"><strong>Rejection Reason:</strong> {business.rejection_reason}</p>
                    </div>
                  )}

                  {/* Free trial renewal banner — show when business has a free featured trial expiring within 10 days or already expired */}
                  {business.featured_free && business.featured_until && (() => {
                    const endsAt = new Date(business.featured_until);
                    const now = new Date();
                    const daysRemaining = Math.round((endsAt - now) / 86400000);
                    if (daysRemaining > 10) return null;
                    const expired = daysRemaining < 0;
                    return (
                      <div
                        className={`mt-4 p-4 rounded-xl border-2 ${expired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-300'}`}
                        data-testid="renewal-banner"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`shrink-0 w-10 h-10 rounded-full ${expired ? 'bg-red-100' : 'bg-amber-100'} flex items-center justify-center`}>
                            <Star className={`w-5 h-5 ${expired ? 'text-red-600' : 'text-amber-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${expired ? 'text-red-900' : 'text-amber-900'} mb-1`}>
                              {expired
                                ? `Your free featured month ended ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? '' : 's'} ago`
                                : daysRemaining === 0
                                  ? 'Your free featured month ends today'
                                  : `Your free featured month ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`}
                            </p>
                            <p className="text-xs text-neutral-700 mb-3">
                              Keep your homepage spotlight and top-of-directory placement — renew now and lock in the $5-off promo through Dec 31.
                            </p>
                            <Link
                              to={`/feature-my-business?business=${business.id}`}
                              className="inline-block px-4 py-2 bg-accent text-white text-sm font-semibold rounded-lg hover:bg-accent/90 transition-colors"
                              data-testid="renew-featured-btn"
                            >
                              Renew Featured Placement →
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {business.status === 'approved' && (
                    <div className="mt-4 pt-4 border-t border-neutral-200 flex gap-3">
                      <Link to={`/businesses/${business.slug || business.id}`} className="btn-secondary text-sm">
                        View Listing
                      </Link>
                      <Link to="/submit-business" className="btn-secondary text-sm">
                        Edit Listing
                      </Link>
                    </div>
                  )}

                  {business.status === 'rejected' && (
                    <div className="mt-4 pt-4 border-t border-neutral-200 flex gap-3">
                      <Link to="/submit-business" className="btn-primary text-sm" data-testid="edit-resubmit-btn">
                        Edit & Resubmit
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-600 mb-4">You haven't created a business listing yet</p>
                  <Link to="/submit-business" className="btn-primary">
                    Create Your Listing
                  </Link>
                </div>
              )}
            </div>

            {/* Analytics Dashboard */}
            {business?.status === 'approved' && analytics && (
              <div className="dashboard-card" data-testid="analytics-section">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#005AA0]" /> Listing Analytics
                  </h2>
                </div>

                {/* Analytics Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-[#005AA0]/5 rounded-xl p-4 text-center" data-testid="stat-today-views">
                    <p className="text-2xl font-bold text-[#005AA0]">{analytics.today_views}</p>
                    <p className="text-xs text-neutral-500 font-medium">Today</p>
                  </div>
                  <div className="bg-[#2A7828]/5 rounded-xl p-4 text-center" data-testid="stat-week-views">
                    <p className="text-2xl font-bold text-[#2A7828]">{analytics.week_views}</p>
                    <p className="text-xs text-neutral-500 font-medium">This Week</p>
                  </div>
                  <div className="bg-[#F08C28]/5 rounded-xl p-4 text-center" data-testid="stat-month-views">
                    <p className="text-2xl font-bold text-[#F08C28]">{analytics.month_views}</p>
                    <p className="text-xs text-neutral-500 font-medium">This Month</p>
                  </div>
                  <div className="bg-neutral-100 rounded-xl p-4 text-center" data-testid="stat-total-views">
                    <p className="text-2xl font-bold text-neutral-800">{analytics.total_views}</p>
                    <p className="text-xs text-neutral-500 font-medium">All Time</p>
                  </div>
                </div>

                {/* Views Chart */}
                <div className="bg-neutral-50 rounded-xl p-4" data-testid="views-chart">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3">Page Views (Last 30 Days)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={analytics.daily_views}>
                      <defs>
                        <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#005AA0" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#005AA0" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                        tickFormatter={(d) => { const parts = d.split('-'); return `${parts[1]}/${parts[2]}`; }}
                        interval={6}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                        labelFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <Area
                        type="monotone"
                        dataKey="views"
                        stroke="#005AA0"
                        strokeWidth={2}
                        fill="url(#viewsGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Top Referrers */}
                {analytics.top_referrers?.length > 0 && (
                  <div className="mt-4" data-testid="top-referrers">
                    <h3 className="text-sm font-semibold text-neutral-700 mb-3">Top Traffic Sources</h3>
                    <div className="space-y-2">
                      {analytics.top_referrers.map((ref, i) => {
                        const maxCount = analytics.top_referrers[0]?.count || 1;
                        const label = ref.source === 'direct' ? 'Direct' : (() => { try { return new URL(ref.source).hostname; } catch { return ref.source; } })();
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-neutral-500 w-24 truncate">{label}</span>
                            <div className="flex-1 bg-neutral-100 rounded-full h-2 overflow-hidden">
                              <div
                                className="h-full bg-[#005AA0] rounded-full transition-all"
                                style={{ width: `${(ref.count / maxCount) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-xs font-semibold text-neutral-600 w-8 text-right">{ref.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reviews summary */}
            {business?.status === 'approved' && reviewStats && (
              <div className="dashboard-card" data-testid="reviews-summary">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#005AA0]" /> Customer Reviews
                  </h2>
                  {business?.slug && (
                    <Link to={`/businesses/${business.slug}`} className="text-sm text-primary hover:underline">
                      View on listing →
                    </Link>
                  )}
                </div>
                {reviewStats.count > 0 ? (
                  <div className="flex items-center gap-4">
                    <div className="text-4xl font-bold text-neutral-900">{reviewStats.average_rating?.toFixed(1)}</div>
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} className={`w-4 h-4 ${(reviewStats.average_rating || 0) >= n ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'}`} />
                        ))}
                      </div>
                      <p className="text-sm text-neutral-500">{reviewStats.count} approved review{reviewStats.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500">No reviews yet. Customers can leave reviews on your listing page — they're moderated by Explore MHC before going public.</p>
                )}
              </div>
            )}

            {/* My Events */}
            <div className="dashboard-card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-bold text-neutral-900">My Events</h2>
                <Link
                  to="/submit-event"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-neutral-900 font-semibold hover:bg-accent/90 shadow-sm transition-colors"
                  data-testid="business-submit-event-btn"
                >
                  <Plus className="w-4 h-4" /> Add Event
                </Link>
              </div>
              <p className="text-sm text-neutral-500 mb-6">
                Promote workshops, sales, grand openings, or any event your business is hosting.
              </p>

              {events.length > 0 ? (
                <div className="space-y-4">
                  {events.map((event) => (
                    <div key={event.id} className="flex items-start gap-4 p-4 bg-neutral-50 rounded-xl">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-neutral-900 truncate">{event.event_name}</h3>
                          {getStatusBadge(event.status)}
                        </div>
                        <p className="text-sm text-neutral-600 mb-2">{event.event_date} • {event.category}</p>
                        {event.is_featured && (
                          <span className="badge badge-featured text-xs flex items-center gap-1 w-fit">
                            <Star className="w-3 h-3" /> Featured
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-600 mb-4">No events submitted yet</p>
                  <Link to="/submit-event" className="btn-primary">
                    Submit Your First Event
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="dashboard-card">
              <h3 className="font-bold text-lg mb-4">Quick Actions</h3>
              <div className="space-y-3">
                {!business && (
                  <Link to="/submit-business" className="block p-4 bg-secondary/10 rounded-xl hover:bg-secondary/20 transition-colors">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-secondary" />
                      <span className="font-medium text-secondary">Create Business Listing</span>
                    </div>
                  </Link>
                )}
                {business?.status === 'rejected' && (
                  <Link to="/submit-business" className="block p-4 bg-red-50 rounded-xl hover:bg-red-100 transition-colors" data-testid="quick-action-resubmit">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-red-600">Edit & Resubmit Business</span>
                    </div>
                  </Link>
                )}
                {business?.status === 'approved' && !business?.is_featured && (
                  <Link to={`/feature-my-business?business=${business.id}`} className="block p-4 bg-accent/10 rounded-xl hover:bg-accent/20 transition-colors">
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-accent" />
                      <span className="font-medium text-accent">Feature My Business</span>
                    </div>
                  </Link>
                )}
                <Link to="/submit-event" className="block p-4 bg-accent/10 rounded-xl hover:bg-accent/20 transition-colors" data-testid="quick-action-submit-event">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-accent" />
                    <span className="font-medium text-accent">Add a Business Event</span>
                  </div>
                </Link>
              </div>
            </div>

            {/* Payment History */}
            <div className="dashboard-card">
              <h3 className="font-bold text-lg mb-4">Payment History</h3>
              {(() => {
                const visiblePayments = payments.filter(p => ['paid', 'refunded', 'refund_failed'].includes(p.payment_status));
                return visiblePayments.length > 0 ? (
                  <div className="space-y-3">
                    {visiblePayments.slice(0, 10).map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                        <div>
                          <p className="font-medium text-sm">{payment.metadata?.product_name || payment.product_type}</p>
                          <p className="text-xs text-neutral-500">{new Date(payment.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-neutral-900">${payment.amount}</span>
                          <p className={`text-xs ${
                            payment.payment_status === 'paid' ? 'text-green-600'
                              : payment.payment_status === 'refunded' ? 'text-amber-600'
                              : 'text-neutral-500'
                          }`}>
                            {payment.payment_status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-neutral-500 text-sm">No payments yet</p>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAccount;
